import DOMPurify from 'dompurify';
import {getStack} from '@ns/ns-contentstack-lib';
import {PaymentType} from '@nuskin/design-components';
import {CheckoutModeService, PaymentDto, PaymentService, ShippingService} from "@nuskin/ns-shop";
import {events, RunConfigService} from '@nuskin/ns-util';
import {getConfiguration, getCachedConfigField} from '@nuskin/configuration-sdk';
import {UserService} from "@nuskin/ns-account";
import {OrderType, ShopContext} from "./PaymentContext";
import {demoPaymentTypes, demoPayments, demoPaymentStrings, demoShippingAddress, demoTdcStrings} from './demoData';
import paymentBynderImagesMap from './paymentBynderImagesMap'

const FULFILLED = 'fulfilled';
const REJECTED = 'rejected';

const INPUT = "input";
const AVAILABLEPAYMENTS = 'availablePayments';
const PAYMENTADDED = 'paymentAddedComplete';
const PAYMENTEDITED = 'paymentEditedComplete';
const PAYMENTREMOVED = 'paymentRemovedComplete';
const PAYMENTSETASDEFAULT = 'paymentSetAsDefault';
const PAYMENTERROR = 'paymentError';
const TOTALCLICKS = "paymentTotalClicks";

/**
 * Recursively converts the given object's keys from snake_case to camelCase.
 * @param {any} obj
 * @return {any}
 */
function snakeKeysToCamelKeys(obj) {
    if (!obj || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) return obj.map(snakeKeysToCamelKeys)

    const camelKeyFields = Object.entries(obj).map(([k, v]) => [
        k.replace(/_\w/g, (m) => m[1].toUpperCase()),
        snakeKeysToCamelKeys(v),
    ]);

    return Object.fromEntries(camelKeyFields);
}

const mixin = {
    props: {
        context: {
            type: String,
            default: ShopContext.PROFILE
        },
        demo: {
            type: Boolean,
            default() {
                return false;
            }
        },
        filterForMarket: {
            type: Boolean,
            default() {
                return false;
            }
        },
        orderType: {
            type: String,
            default: OrderType.PROFILE
        },
        translations: {
            type: Object,
            default() {
                return demoPaymentStrings;
            }
        },
        useBillingAddress: {
            type: Boolean,
            default() {
                return false;
            }
        },
        paymentOptions: {
            type: String,
            default() {
                return null;
            }
        },
        csApiKey: {
          type: String,
          required: true
        },
        csDeliveryToken: {
          type: String,
          required: true
        },
        csEnvironment: {
          type: String,
          required: true
        }
    },
    data() {
        return {
            additionalPaymentMethodsInfo: [],
            allAvailablePaymentTypes: [],
            availableCarteBleueTypes: [],
            availableCedynaTypes: [],
            availableCreditTypes: [],
            groupedCreditTypes: [],
            availableSimvTypes: [],
            formCreditTypes: [],
            availablePaymentTypes: [],
            config: {},
            user: {},
            country: null,
            defaultShippingAddress: {},
            isGuest: false,
            language: null,
            payments: [],
            saveNonCreditCards: false,
            selectedPaymentType: -1,
            t: {},
            enableStoredCredentials: false,
            checkoutStrings: demoTdcStrings,
            withAddress: false,
            totalClicks: 0,
            termsOfUseLnk: null,
            resolvePaymentOptions: null,
            rejectPaymentOptions: null
        };
    },
    watch: {
        paymentOptions(newValue) {
            if (typeof newValue === 'string') {
                try {
                    this.paymentOptions = JSON.parse(newValue);
                    if (typeof this.resolvePaymentOptions === 'function') {
                        this.resolvePaymentOptions(this.filterPaymentOptions());
                    }
                } catch (err) {
                    console.error("Payment Options could not be parsed", err);
                    if (typeof this.rejectPaymentOptions === 'function') {
                        this.rejectPaymentOptions('Waiting for payment options errored out!');
                    }
                }
                this.resolvePaymentOptions = this.rejectPaymentOptions = null;
            }
        }
    },
    methods: {
        filterPaymentOptions() {
            try {
                if (this.paymentOptions && Array.isArray(this.paymentOptions)) {
                    const isApplePayEligible = this.checkApplePayEligible();
                    if (!isApplePayEligible) {
                        this.paymentOptions = this.paymentOptions.filter(po => po.id !== PaymentType.APLP);
                    }
                    const isGooglePayEligible = !this.inWebView();
                    if (!isGooglePayEligible) {
                        this.paymentOptions = this.paymentOptions.filter(po => po.id !== PaymentType.GGLP);
                    }
                }
            } catch (err) {
                console.error("Payment Options could not be parsed", err);
            }
            return this.paymentOptions;
        },
        /**
         * Gets the allowed payment types based on context and order type.
         */
        getAllowedPaymentTypes() {
            return new Promise(async (resolve, reject) => {
                if (this.orderType === OrderType.PROFILE) {
                    let allAvailablePaymentTypes = [];

                    if (this.demo) {
                        resolve(demoPaymentTypes);
                    } else {
                        try {
                            allAvailablePaymentTypes = await PaymentService.getAllowedPaymentTypes({
                                shopCntxt: ShopContext[this.context],
                                orderType: this.orderType
                            });
                            resolve(allAvailablePaymentTypes);
                        } catch (err) {
                            reject(err);
                        }
                    }
                } else {
                    this.resolvePaymentOptions = resolve;
                    this.rejectPaymentOptions = reject;
                }
            });
        },

        inWebView() {
            const standalone = window.navigator.standalone,
            userAgent = window.navigator.userAgent.toLowerCase(),
            safari = /safari/.test(userAgent),
            ios = /iphone|ipod|ipad/.test(userAgent);

            if (ios) {
                if (!standalone && !safari) {
                    //ios webview
                    return true;
                }
            } else if(/wv/.test(userAgent)) {
                // Android webview
                return true;
            }
            return false;
        },

        checkApplePayAvailable() {
            return this.paymentOptions && Array.isArray(this.paymentOptions) && this.paymentOptions.some(p => p.id == PaymentType.APLP);
        },

        checkApplePayEligible() {
            return this.checkApplePayAvailable() && window.ApplePaySession && window.ApplePaySession.supportsVersion(3) && window.ApplePaySession.canMakePayments();
        },

        /**
         * Loads the logged in user's save payments
         */
        async getSavedPayments() {
            let payments = [];

            if (this.demo) {
                payments = demoPayments;
            } else {
                let paymentPromise = this.filterForMarket
                    ? PaymentService.getSavedPaymentsInCurrentMarket()
                    : PaymentService.getSavedPayments(true);

                payments = await paymentPromise;
            }
            return payments;
        },

        /**
         * Gets the default billing address for the logged in user.  If there
         * isn't one flagged as default then returns the first one found.
         */
        async getDefaultBillingAddress() {
            if (this.demo) {
                this.defaultShippingAddress = demoShippingAddress;
            } else if (Object.keys(this.defaultShippingAddress).length < 2) {
                try {
                    const response = await ShippingService.getShippingAddressList();
                    const shippingAddressList =
                        response && response.ShippingAddressList
                            ? response.ShippingAddressList
                            : [];
                    this.defaultShippingAddress = shippingAddressList.reduce(
                        (address, item) => {
                            if (!address.city || item.defaultShipping) {
                                address = this.adaptToBillingAddress(item);
                            }
                            return address;
                        },
                        { country: this.country }
                    );
                } catch (e) {
                    this.defaultShippingAddress = { country: this.country };
                    // could show alert here but this is just getting the default address.
                    // If there are issues elsewhere an alert will be show there.
                    console.error("Error getting default address", e);
                }
            }
        },

        /**
         * Converts a shipping address to a billing address
         * @param {Object} shippingAddress
         * @returns {Object} billingAddress
         */
        adaptToBillingAddress(shippingAddress) {
            return {
                city: shippingAddress.shippingCity,
                address1: shippingAddress.shippingAddress1,
                address2: shippingAddress.shippingAddress2,
                district: shippingAddress.shippingCounty,
                region: shippingAddress.shippingState,
                country: shippingAddress.shippingCountry,
                postalCode: shippingAddress.shippingPostalCode,
                email: shippingAddress.shippingEmail
            }
        },

        /**
         * Sets additional information on the given payments used by the HTML:
         *  - expiring flag/date
         *  - expired flag
         *  - canEdit boolean
         *  - paymentMethodText
         */
        setExtraFieldsOnPayment(pmtId) {
            if (pmtId) {
                const p = this.payments.find(pmt => pmt.bePaymentTypeId === pmtId);
                if (p) {
                    // set expired/expiring fields
                    p.canEdit = false;
                    p.cvvRequired = this.findAvailablePaymentType(p.paymentTypeId).cvvRequired;
                    if (p.paymentTypeId === PaymentType.CBLU || this.availableCreditTypes.some(creditType => creditType.id === p.paymentTypeId)) {
                        const cardExpDate = new Date(`20${p.expYear}`, p.expMonth);
                        const now = new Date();

                        p.expired = null;
                        p.expiring = null;
                        p.expiringSoon = false;
                        if (now.getTime() >= cardExpDate.getTime()) {
                            p.expired = `${p.expMonth}/${p.expYear}`;
                        } else {
                            p.expiring = `${p.expMonth}/${p.expYear}`;
                            p.expiringSoon =
                                cardExpDate.getFullYear() === now.getFullYear() &&
                                cardExpDate.getMonth() === now.getMonth();
                        }

                        p.canEdit = true;
                        p.editExpirationDate = false;
                    }
                    if (this.additionalPaymentMethodsInfo.length > 0) {
                        this.additionalPaymentMethodsInfo.map(info => {
                            if (parseInt(info.paymentTypeId) === p.paymentTypeId) {
                                p.paymentMethodText = info.paymentMethodShortDesc;
                                p.paymentTypeDescription = info.paymentMethodText;
                            }
                        });
                    }
                }
            }
        },

        /**
         * Updates additional payment information.
         */
        updatePaymentInfo() {
            this.availablePaymentTypes = this.filterAvailableCcPaymentTypes(false);
            this.availableCreditTypes = this.filterAvailableCcPaymentTypes();
            this.groupedCreditTypes = this.availableCreditTypes.filter((ccType) => ccType.id != PaymentType.SIMV && ccType.id != PaymentType.CBLU);
            const simv = this.findAvailablePaymentType(PaymentType.SIMV);
            this.availableSimvTypes = simv ? [simv] : [];
            const cblu = this.findAvailablePaymentType(PaymentType.CBLU);
            this.availableCarteBleueTypes = cblu ? [cblu] : [];
            this.availableCedynaTypes = [this.findAvailablePaymentType(PaymentType.CEDN)];
            this.t.cedynaFormTitle = this.allAvailablePaymentTypes.reduce((label, item) => {
                if (item.id === PaymentType.CEDN) {
                    label = item.localizedName;
                }
                return label;
            }, '');
            this.selectedPaymentType = this.availableCreditTypes.length > 0 ? 0 : this.availablePaymentTypes[0].id;
            this.withAddress = this.paymentTypeIsWithAddress(this.selectedPaymentType);
            this.payments = this.payments.filter(
                payment => this.hasAvailablePaymentType(payment.paymentTypeId)
            );

            // Don't show the add payment type option if there is one already saved unless it is carte bleue or SEPA
            const multiplePaymentsAllowed = [PaymentType.CBLU, PaymentType.SDD];
            this.availablePaymentTypes = this.availablePaymentTypes.filter(
                pType => multiplePaymentsAllowed.includes(pType.id) || !this.payments.some(p => p.paymentTypeId === pType.id)
            );
            this.payments.forEach(p => {
                this.setExtraFieldsOnPayment(p.bePaymentTypeId);
            }, this);
        },

        /**
         * Loads TDC strings from the checkout page.
         */
        async getAdditionalPaymentInfoForm() {
            let otherPaymentInfo = [];
            if (typeof this.checkoutStrings !== "undefined" && 'otherPaymentMethods' in this.checkoutStrings) {
                otherPaymentInfo = this.checkoutStrings.otherPaymentMethods;
            }
            return otherPaymentInfo;
        },

        /**
         * Gets all payment info including allowed payment types and the
         * saved payments for the logged in user.  Also sets up other
         * payment information used in the component.
         */
        async getAllPaymentInfo() {
            let promises = [
                this.getAllowedPaymentTypes(),
                this.getSavedPayments(),
                this.getDefaultBillingAddress()
            ];

            let results = await Promise.allSettled(promises);

            if (!(results[0].status === REJECTED || results[1].status === REJECTED)) {
                this.allAvailablePaymentTypes = results[0].value;
                this.$emit(AVAILABLEPAYMENTS, this.allAvailablePaymentTypes);
                this.payments = results[1].value;
                if (this.allAvailablePaymentTypes.some(pType => pType.formName === 'emptyPayment')) {
                    this.additionalPaymentMethodsInfo = await this.getAdditionalPaymentInfoForm();
                }
                this.updatePaymentInfo();
            }

            return [results[0].status === FULFILLED, results[1].status === FULFILLED];
        },

        hideDefaultCheckbox() {
            return this.selectedPaymentType == 54;
        },

        /**
         * Unsets existing default payment, not the payment passed in.
         */
        async unsetDefaultPayment(payment) {
            // if the payment has default payment set and is not the current payment being edited then remove the default flag
            let p = this.payments.find(p => (p.defaultPayment === true && p.bePaymentTypeId !== payment.bePaymentTypeId));
            if (p) {
                p.defaultPayment = false;
                if (this.isSavedPayment(payment.bePaymentTypeId)) {
                    let paymentDto = new PaymentDto(p);
                    try {
                        await PaymentService.editSavedPayment(paymentDto);
                    } catch (err) {
                        p.defaultPayment = true;
                        events.tag(PAYMENTERROR, {
                            context: this.context,
                            errorCategory: 'Failed to unset the current default payment: ' + err
                        });
                        throw err;
                    }
                }
            }
        },
        async updateDefaultPayment(payment, method = 'notSet') {
            if (payment.defaultPayment === false) {
                if (this.isSavedPayment(payment.bePaymentTypeId)) {
                    this.unsetDefaultPayment(payment);
                    payment.defaultPayment = true;
                    await PaymentService.editSavedPayment(new PaymentDto(payment));
                    this.updateDefaultPaymentOrder();
                    this.updateAnalytics(PAYMENTSETASDEFAULT, payment.paymentTypeDescription, method);
                } else {
                    this.unsetDefaultPayment(payment);
                    payment.defaultPayment = true;
                    this.updateDefaultPaymentOrder();
                    this.updateAnalytics(PAYMENTSETASDEFAULT, payment.paymentTypeDescription, method);
                }
            }
        },

        /**
         *
         * @param {string} tag
         * @param {string} description
         * @param {string} method
         */
        updateAnalytics(tag, description, method = '') {
            events.tag(tag, {
                context: this.context,
                paymentType: description,
                method: method
            });
        },

        /**
         * Send total clicks in the payment section to analytics
         */
        publishTotalClicks() {
            events.tag(TOTALCLICKS, {
                context: this.context,
                totalClicks: this.totalClicks.toString()
            });
        },

        /**
         * This functions registers listeners for a page change and will send total clicks to gtm
         * on a page change
         */
        subscribeToPageChange() {
            // If this component lives in a vue application publish total clicks if route changes
            if (this.$router) {
                this.$router.beforeEach((to, from, next) => {
                    if (this.totalClicks > 0) {
                        this.publishTotalClicks();
                        this.totalClicks = 0;
                    }
                    next();
                });
            }
            // If we leave the page publish the total clicks to gtm
            window.addEventListener("beforeunload", this.publishTotalClicks);
            //Before we change routes in checkout publish totalClicks event to gtm
            events.subscribe(
                events.shop.CHECKOUT_ROUTE_CHANGING,
                this.publishTotalClicks
            );
        },

        /**
         * Saves an edited payment
         *
         * @param {*} payment
         */
        async editPayment(payment, method = 'notSet') {
            let pIndex;
            let paymentDto = new PaymentDto(payment);
            if (paymentDto.defaultPayment) {
                await this.unsetDefaultPayment(paymentDto);
            }
            if (this.isSavedPayment(paymentDto.bePaymentTypeId)) {
                const response = await PaymentService.editSavedPayment(paymentDto);
                pIndex = this.payments.findIndex(p => p.bePaymentTypeId === response.bePaymentTypeId);
                // If the user updated the payment to be the defaultPayment
                if (response.defaultPayment && !this.payments[pIndex].defaultPayment) {
                    this.updateAnalytics(PAYMENTSETASDEFAULT, response.paymentTypeDescription, method);
                }
                this.updateAnalytics(PAYMENTEDITED, payment.paymentTypeDescription);
                this.$set(this.payments, pIndex, response);
            } else {
                pIndex = this.payments.findIndex(p => p.bePaymentTypeId === paymentDto.bePaymentTypeId);
                this.$set(this.payments, pIndex, paymentDto);
            }
            return this.payments[pIndex];
        },

        /**
         * Saves a new payment
         *
         * @param {Object} payment
         * @param {string} method
         */
        async addPayment(payment, method = 'notSet', saveToProfile = true) {
            let retVal;
            // make sure we have a country for non credit payments
            payment.countryCode = payment.countryCode || RunConfigService.getRunConfig().country;
            let paymentDto = new PaymentDto(payment);

            if (paymentDto.defaultPayment) {
                await this.unsetDefaultPayment(paymentDto);
            }
            if (UserService.isLoggedIn() && saveToProfile) {
                let response = await PaymentService.addPayment(paymentDto);
                this.payments.push(response);
                if (response.defaultPayment) {
                    this.updateAnalytics(PAYMENTSETASDEFAULT, response.paymentTypeDescription, method);
                }
                this.updateAnalytics(PAYMENTADDED, response.paymentTypeDescription, method);
                retVal = response.bePaymentTypeId;
                this.onSavePaymentRedirect(response);
            } else {
                //Assign a bePaymentTypeId as a unique identifier
                paymentDto.bePaymentTypeId = saveToProfile ? Date.now() : -(Date.now());
                this.payments.push(paymentDto);
                retVal = paymentDto.bePaymentTypeId;
            }
            this.updatePaymentInfo();
            return retVal;
        },

        /**
         * Returns true if the payment is saved to the payment service
         * If bePaymentTypeId is < 0 then the id was assigned when added just for reference
         * @param bePaymentTypeId
         * @returns {boolean}
         */
        isSavedPayment(bePaymentTypeId) {
            return bePaymentTypeId > 0 && !this.demo;
        },

        emitSelectedPayment(payment) {
            let sPayment = {...payment};
            if (!this.isSavedPayment(sPayment.bePaymentTypeId)) {
                sPayment.bePaymentTypeId = null;
            }
            this.$emit(INPUT, sPayment);
        },

        /**
         * Deletes payment from the server if it is saved and removes it
         * from the payments list.  Also logs the event in the datalayer.
         *
         * @param {*} paymentId - ID of the payment to delete
         * @param {boolean} fromForm - true if deleting from edit form
         */
        async deletePayment(paymentId, fromForm) {
            let deleted = true;

            if (this.isSavedPayment(paymentId)) {
                try {
                    await PaymentService.deleteSavedPayment(paymentId);
                } catch (err) {
                    console.error(`err: ${err}`);
                    events.tag(PAYMENTERROR, {
                        context: this.context,
                        errorCategory: 'Failed to delete payment: ' + err
                    });
                    deleted = false;
                }
            }
            if (deleted) {
                const index = this.payments.findIndex(payment => payment.bePaymentTypeId === paymentId);
                events.tag(PAYMENTREMOVED, {
                    context: this.context,
                    paymentType: this.payments[index].paymentTypeDescription,
                    method: fromForm ? 'deleteButton' : 'deleteMenuOption'
                });
                this.$delete(this.payments, index);
                this.updatePaymentInfo();
            }

            return deleted;
        },

        /**
         * Converts address format to/from form format or saved format
         *
         * @param {*} inAddress - address object to convert format
         * @param {boolean} toForm - true if converting to form format, false otherwise
         */
        getBillingAddress(inAddress, toForm = true) {
            let address = null;

            if (inAddress && this.withAddress) {
                if (toForm) {
                    address = inAddress.country ? inAddress : {
                        country: inAddress.countryCode,
                        postalCode: inAddress.paymentPostalCode,
                    }
                } else {
                    address = inAddress.countryCode ? inAddress : {
                        countryCode: inAddress.country,
                        paymentPostalCode: inAddress.postalCode,
                    }
                }
                address.city = inAddress.city;
                address.address1 = inAddress.address1;
                address.address2 = inAddress.address2;
                address.district = inAddress.district;
                address.region = inAddress.region;
                address.email = inAddress.email;
            }

            return address;
        },

        /**
         * Converts payment format to/from form format or saved format
         *
         * @param {*} inData - payment object to convert format
         * @param {boolean} setAsDefault - default payemnt flag, used when
         *                                  converting to saved payment format
         * @param {*} toForm - true if converting to form format, false otherwise
         */
        getPaymentData(inData, setAsDefault = false, toForm = true) {
            let paymentData = null;

            if (inData) {
                if (toForm) {
                    paymentData = inData.cardHolderName ? inData : {
                        cardHolderName: inData.paymentName,
                        cardNumber: inData.paymentNumber
                    }
                } else {
                    paymentData = inData.paymentName ? inData : {
                        paymentName: inData.cardHolderName,
                        paymentNumber: inData.cardNumber,
                        defaultPayment: setAsDefault
                    }
                }
                paymentData.bePaymentTypeId = inData.bePaymentTypeId;
                paymentData.paymentTypeId = inData.paymentTypeId;
                paymentData.paymentTypeDescription = inData.paymentTypeDescription;
                paymentData.expMonth = inData.expMonth;
                paymentData.expYear = inData.expYear;
            }
            return paymentData;
        },

        /**
         * Initializes translations for the payment section. Puts all translations
         * on this.t.  There is demo data provided from the translations
         */
        async getTranslations() {
            if (this.demo) {
                this.t = this.translations;
            } else {
                /** @type {import('contentstack').Stack} */
                const stack = getStack();

                const lang = this.language === 'in' ? 'id' : this.language;
                const csTranslations = await stack
                    .ContentType("payment_component")
                    .Query()
                    .language(`${this.country}-${lang}`)
                    .includeFallback()
                    .toJSON()
                    .findOne();

                /*
                The keys "update" and "save" are reserved words in Contentstack.
                Therefore, those two are represented in the content type as
                "update_label" and "save_label".
                */
                csTranslations.update = csTranslations.update_label;
                csTranslations.save = csTranslations.save_label;

                const translations = snakeKeysToCamelKeys(csTranslations);

                this.t = translations;
                this.checkoutStrings = translations;
            }
        },

        updateDefaultPaymentOrder() {
            if (Array.isArray(this.payments)) {
                let defaultIndex = this.payments.findIndex(p => p.defaultPayment);
                let p = { ...this.payments[defaultIndex] };
                if (defaultIndex > 0) {
                    this.$delete(this.payments, defaultIndex);
                    this.payments.unshift(p);
                }
            }
        },

        getCcLastFour(ccNumber) {
            return typeof ccNumber === 'string' ? ccNumber.substring(ccNumber.length - 4) : '';
        },

        getCardIcon(id) {
            return paymentBynderImagesMap[id] || '';
        },

        filterAvailableCcPaymentTypes(include = true) {
            return this.allAvailablePaymentTypes.filter(pt => PaymentType.credit.includes(pt.id) === include)
        },

        findAvailablePaymentType(ptId) {
            return this.allAvailablePaymentTypes.find(pt => ptId === pt.id);
        },

        hasAvailablePaymentType(ptId) {
            return this.allAvailablePaymentTypes.some(pt => ptId === pt.id);
        },

        paymentTypeFormName: function (ptId) {
            let paymentType = this.findAvailablePaymentType(ptId);
            return paymentType ? paymentType.formName : "";
        },

        emptyPaymentFormName: function(ptId) {
            return this.paymentTypeFormName(ptId) === 'emptyPayment';
        },

        paymentTypeIsWithAddress(ptId) {
            return this.useBillingAddress && this.paymentTypeFormName(ptId).includes('WithAddress');
        },

        isCreditCardPaymentType(ptId) {
            return PaymentType.credit.includes(ptId);
        },

        resetForms() {
            if (this.$refs.addAddressForm) {
                this.$refs.addAddressForm.reset();
            }
            if (this.$refs.addSimvForm) {
                this.$refs.addSimvForm.reset();
            }
            if (this.$refs.editAddressForm) {
                this.$refs.editAddressForm.reset();
            }
            if (this.$refs.cedynaForm) {
                this.$refs.cedynaForm.reset();
            }
            if (this.$refs.editCreditCardForm) {
                this.$refs.editCreditCardForm.reset();
            }
            if (this.$refs.addCreditCardForm) {
                this.$refs.addCreditCardForm.reset();
            }
        },

        getFirstAvailablePaymentType() {
            return this.availableCreditTypes.length > 0 ? this.availableCreditTypes[0].id :
                this.availablePaymentTypes.length > 0 ? this.availablePaymentTypes[0].id :
                    null;
        },

        canAddPayment() {
            return this.availableCreditTypes.length > 0 || this.availablePaymentTypes.length > 0
        },

        samePaymentType(pt1, pt2) {
            // 0 in the statement below represents the grouped credit card radio button.
            return pt1 === pt2 ||
                ((pt1 === PaymentType.SIMV || pt1 === 0 || pt1 === PaymentType.CBLU) &&
                (pt2 === PaymentType.SIMV || pt2 === 0 || pt2 === PaymentType.CBLU));
        },

        setFormCreditTypes(paymentType) {
            if (paymentType === 0) {    // 0 represents the grouped credit card radio button
                this.formCreditTypes = this.groupedCreditTypes;
            } else if (paymentType === PaymentType.SIMV) {
                this.formCreditTypes = this.availableSimvTypes;
            } else if (paymentType === PaymentType.CBLU) {
                this.formCreditTypes = this.availableCarteBleueTypes;
            }
        },

        /**
         * Polyfill for Promise.allSettled since it is not supported in all browsers
         */
        initAllSettled() {
            if (!Promise.allSettled) {
                Promise.allSettled = (promises) =>
                    Promise.all(
                        promises.map((promise) =>
                            promise
                                .then((value) => {
                                    return {
                                        status: FULFILLED,
                                        value
                                    };
                                })
                                .catch((reason) => {
                                    return {
                                        status: REJECTED,
                                        reason
                                    };
                                })
                        )
                    );
            }
        },

        async initMixin() {
            await getConfiguration(['Checkout', 'Account', 'Basic']);
            this.country = this.demo ? 'US' : RunConfigService.getRunConfig().country;
            this.language = this.demo ? 'en' : RunConfigService.getRunConfig().language;
            this.enableEmandates = getCachedConfigField('enableEmandates');
            this.user = UserService.getUser();
            this.accountHolderName = this.user && (`${this.user.firstName} ${this.user.lastName}`);
            this.saveNonCreditCards = true;
            this.enableStoredCredentials = getCachedConfigField('enableStoredCredentials');
            this.isGuest = this.demo ? true : CheckoutModeService.isGuestCheckout();
            this.initAllSettled();
            await this.getTranslations();
            if (this.enableStoredCredentials) await this.getTermsOfUseLink();
        },


        async getTermsOfUseLink() {
            const { baseUrl, language, country } = await RunConfigService.getRunConfig();

            this.termsOfUseLnk = country === 'JP'
                ? `${baseUrl}/content/markets/${language}_${country}/corporate/terms.html`
                : `${baseUrl}/content/nuskin/${language}_${country}/corporate/terms.html`
        },

        updateShowPaymentsNotAvailable(value) {
            this.showPaymentsNotAvailable = value;
        },

        sanitizeHTML(content) {
            // To sanitize v-html fields
            return DOMPurify.sanitize(content);
        },

        onSavePaymentRedirect(payment) {
            // Early return if there is no redirect Url.
            if (!payment.redirectURL) {
                return;
            }

            let redirectURL = this.generateRedirectURL(payment.redirectURL);

            window.location = redirectURL;
        },

        generateRedirectURL(url) {
            let landingUrl = encodeURIComponent(window.location.href);

            // Append the landing page URL
            // Example of payment.redirectUrl: "redirectUrl":
            //  "<redirect_url>&landingUrl=https://test.nuskin.com/static/checkout/checkout.html#/en_DE/orderSummary"
            let urlObj = new URL(url);
            urlObj.searchParams.set('landingUrl', landingUrl);
            return urlObj.toString();
        }
    }
};

export default mixin;
